'use client';

import { c as _c } from "react/compiler-runtime";
import { BlobProvider, Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { FileText, Loader2 } from 'lucide-react';
import { useLocalization } from '@/app/(app)/_providers/localization/LocalizationProvider';
import { Button } from '@/components/ui/button';
import type { ConfigData, EquipmentCategory } from './ConfigPanel';

// Approximate theme colors (from globals.css) for PDF
const colors = {
  background: '#ffffff',
  foreground: '#252525',
  mutedForeground: '#6b7280',
  border: '#e5e7eb',
  primary: '#252525',
  primaryForeground: '#fafafa',
  muted: '#f3f4f6'
};
const CATEGORY_COLOR: Record<EquipmentCategory, string> = {
  display: '#3b82f6',
  audio: '#22c55e',
  control: '#f59e0b',
  network: '#8b5cf6',
  source: '#06b6d4',
  processing: '#f43f5e',
  infrastructure: '#64748b',
  mounting: '#f97316',
  cabling: '#14b8a6'
};

// Use built-in PDF fonts (Helvetica, Courier). Custom TTF/WOFF work but Google Fonts
// only provide WOFF2 (unsupported in browser) or variable TTF (unsupported by react-pdf).
const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontFamily: 'Helvetica',
    fontSize: 11,
    backgroundColor: colors.background,
    color: colors.foreground
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 20
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.foreground
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 9999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.muted
  },
  statusText: {
    fontSize: 10,
    color: colors.mutedForeground
  },
  section: {
    marginBottom: 24
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: 10
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.foreground
  },
  sectionCount: {
    fontSize: 13,
    color: colors.mutedForeground
  },
  overviewGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  overviewItem: {
    width: '50%',
    paddingVertical: 8,
    paddingRight: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  overviewLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: colors.mutedForeground,
    marginBottom: 2
  },
  overviewValue: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.foreground
  },
  overviewValueMono: {
    fontFamily: 'Courier'
  },
  equipmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: 10
  },
  categoryDot: {
    width: 6,
    height: 6,
    borderRadius: 3
  },
  equipmentDetails: {
    flex: 1
  },
  equipmentName: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.foreground
  },
  equipmentMeta: {
    fontSize: 10,
    color: colors.mutedForeground,
    marginTop: 2
  },
  equipmentQty: {
    fontFamily: 'Courier',
    fontSize: 10,
    color: colors.mutedForeground
  },
  signalFlowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6
  },
  signalNode: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },
  signalNodeName: {
    fontFamily: 'Courier',
    fontSize: 10,
    fontWeight: 600,
    color: colors.foreground,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 4
  },
  signalNodeLabel: {
    fontSize: 10,
    color: colors.mutedForeground
  },
  signalArrow: {
    fontSize: 12,
    color: colors.mutedForeground
  },
  noteRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  noteTag: {
    fontFamily: 'Courier',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: colors.mutedForeground,
    backgroundColor: colors.muted,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginRight: 6
  },
  noteContent: {
    fontSize: 12,
    color: colors.mutedForeground,
    lineHeight: 1.5,
    marginTop: 4
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 32,
    right: 32,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border
  },
  footerUpdated: {
    fontSize: 10,
    color: colors.mutedForeground
  }
});
function ConfigPanelPdfDocument(t0) {
  const $ = _c(28);
  const {
    data,
    signalFlowImageUrl,
    labels
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      flexDirection: "row",
      alignItems: "center",
      gap: 10
    };
    t2 = <Text style={styles.headerTitle}>Configuration</Text>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== data.status) {
    t3 = data.status && <View style={styles.statusBadge}><Text style={styles.statusText}>{data.status}</Text></View>;
    $[2] = data.status;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <View style={styles.header} fixed={true}><View style={t1}>{t2}{t3}</View></View>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== data.overview || $[7] !== labels) {
    t5 = data.overview.length > 0 && <View style={styles.section}><View style={styles.sectionHeader}><Text style={styles.sectionTitle}>{labels.projectOverview}</Text></View><View style={styles.overviewGrid}>{data.overview.map(_temp)}</View></View>;
    $[6] = data.overview;
    $[7] = labels;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== data.equipment || $[10] !== labels) {
    t6 = data.equipment.length > 0 && <View style={styles.section}><View style={styles.sectionHeader}><Text style={styles.sectionTitle}>{labels.equipment}</Text><Text style={styles.sectionCount}>{data.equipment.length} {labels.items}</Text></View>{data.equipment.map(_temp2)}</View>;
    $[9] = data.equipment;
    $[10] = labels;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== data.signalFlow.length || $[13] !== labels || $[14] !== signalFlowImageUrl) {
    t7 = data.signalFlow.length > 0 && <View style={styles.section}><View style={styles.sectionHeader}><Text style={styles.sectionTitle}>{labels.signalFlow}</Text></View><View style={styles.signalFlowRow}>{signalFlowImageUrl ? <Image src={signalFlowImageUrl} style={{
          maxWidth: 520,
          maxHeight: 400
        }} /> : <Text style={styles.noteContent}>{labels.renderingDiagram}</Text>}</View></View>;
    $[12] = data.signalFlow.length;
    $[13] = labels;
    $[14] = signalFlowImageUrl;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== data.notes || $[17] !== labels) {
    t8 = data.notes.length > 0 && <View style={styles.section}><View style={styles.sectionHeader}><Text style={styles.sectionTitle}>{labels.notes}</Text></View>{data.notes.map(_temp3)}</View>;
    $[16] = data.notes;
    $[17] = labels;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = data.updatedAt || labels.updatedJustNow;
  let t10;
  if ($[19] !== t9) {
    t10 = <View style={styles.footer} fixed={true}><Text style={styles.footerUpdated}>{t9}</Text></View>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8) {
    t11 = <Document><Page size="A4" style={styles.page}>{t4}{t5}{t6}{t7}{t8}{t10}</Page></Document>;
    $[21] = t10;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp3(note, i_0) {
  return <View key={`${note.tag}-${i_0}`} style={styles.noteRow}><Text style={styles.noteTag}>{note.tag}</Text><Text style={styles.noteContent}>{note.content}</Text></View>;
}
function _temp2(item_0, i) {
  return <View key={`${item_0.name}-${i}`} style={styles.equipmentRow}><View style={[styles.categoryDot, {
      backgroundColor: CATEGORY_COLOR[item_0.category]
    }]} /><View style={styles.equipmentDetails}><Text style={styles.equipmentName}>{item_0.name}</Text><Text style={styles.equipmentMeta}>{item_0.meta}</Text></View><Text style={styles.equipmentQty}>×{item_0.quantity}</Text></View>;
}
function _temp(item) {
  return <View key={item.label} style={styles.overviewItem}><Text style={styles.overviewLabel}>{item.label}</Text><Text style={[styles.overviewValue, ...(item.mono ? [styles.overviewValueMono] : [])]}>{item.value}</Text></View>;
}
const DEFAULT_FILENAME = 'configuration.pdf';
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
export type ConfigPanelPdfExportProps = {
  data: ConfigData | null;
  signalFlowImageUrl?: string | null;
  filename?: string;
};
export function ConfigPanelPdfExport(t0) {
  const $ = _c(26);
  const {
    data,
    signalFlowImageUrl: t1,
    filename: t2
  } = t0;
  const signalFlowImageUrlProp = t1 === undefined ? null : t1;
  const filename = t2 === undefined ? DEFAULT_FILENAME : t2;
  const {
    messages
  } = useLocalization();
  const hasSignalFlow = Boolean(data?.signalFlow?.length);
  const isPreparing = hasSignalFlow && !signalFlowImageUrlProp;
  if (!data) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <FileText className="h-3.5 w-3.5" />;
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    let t4;
    if ($[1] !== messages.config.exportPdf) {
      t4 = <Button type="button" disabled={true}>{t3}{messages.config.exportPdf}</Button>;
      $[1] = messages.config.exportPdf;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    return t4;
  }
  const readyToExport = !isPreparing;
  const t3 = hasSignalFlow ? signalFlowImageUrlProp : null;
  let t4;
  if ($[3] !== messages.config.equipment || $[4] !== messages.config.items || $[5] !== messages.config.notes || $[6] !== messages.config.projectOverview || $[7] !== messages.config.renderingDiagram || $[8] !== messages.config.signalFlow || $[9] !== messages.config.updatedJustNow) {
    t4 = {
      projectOverview: messages.config.projectOverview,
      equipment: messages.config.equipment,
      items: messages.config.items,
      signalFlow: messages.config.signalFlow,
      notes: messages.config.notes,
      renderingDiagram: messages.config.renderingDiagram,
      updatedJustNow: messages.config.updatedJustNow
    };
    $[3] = messages.config.equipment;
    $[4] = messages.config.items;
    $[5] = messages.config.notes;
    $[6] = messages.config.projectOverview;
    $[7] = messages.config.renderingDiagram;
    $[8] = messages.config.signalFlow;
    $[9] = messages.config.updatedJustNow;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== data || $[12] !== t3 || $[13] !== t4) {
    t5 = <ConfigPanelPdfDocument data={data} signalFlowImageUrl={t3} labels={t4} />;
    $[11] = data;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const document = t5;
  let t6;
  if ($[15] !== filename || $[16] !== isPreparing || $[17] !== messages.config.buttonError || $[18] !== messages.config.buttonGenerating || $[19] !== messages.config.buttonPreparing || $[20] !== messages.config.exportPdf || $[21] !== readyToExport) {
    t6 = t7 => {
      const {
        blob,
        loading,
        error
      } = t7;
      const handleClick = () => {
        if (blob) {
          downloadBlob(blob, filename);
        }
      };
      const disabled = !readyToExport || loading;
      const buttonLabel = !readyToExport ? messages.config.buttonPreparing : error ? messages.config.buttonError : loading ? messages.config.buttonGenerating : messages.config.exportPdf;
      return <Button type="button" onClick={handleClick} disabled={disabled}>{loading || isPreparing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}{buttonLabel}</Button>;
    };
    $[15] = filename;
    $[16] = isPreparing;
    $[17] = messages.config.buttonError;
    $[18] = messages.config.buttonGenerating;
    $[19] = messages.config.buttonPreparing;
    $[20] = messages.config.exportPdf;
    $[21] = readyToExport;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== document || $[24] !== t6) {
    t7 = <BlobProvider document={document}>{t6}</BlobProvider>;
    $[23] = document;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}